import { useState } from 'react';
import Gravatar from 'react-gravatar';
import { Intent } from '@blueprintjs/core';
import {
  Alert,
  Button,
  Dialog,
  Heading,
  IconButton,
  Modal,
  ModalOverlay,
  Tag,
  TagGroup,
  TagList,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { Notifications } from '../../notifications/components/Notifications';
import { observableToPromise } from '../../shared/observableToPromise';
import { postMemberMfaReset } from '../api';
import { type Account, getMemberDisplayName, type Member } from '../model';

import { AuthMethodTag } from './AuthMethodTag';

import './members.scss';

type ResetMfaDrawerProps = Readonly<{
  isOpen: boolean;
  member: Member;
  account: Account;
  onClose: () => void;
  onSuccess: () => void;
  marketplaceProvider?: string;
}>;

export function ResetMfaDrawer({
  isOpen,
  member,
  account,
  onClose,
  onSuccess,
  marketplaceProvider,
}: ResetMfaDrawerProps) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const memberName = getMemberDisplayName(member);
  const firstName = memberName.split(' ')[0];

  const handleReset = async () => {
    setIsPending(true);
    setError(null);
    try {
      await observableToPromise(postMemberMfaReset(account, member));
      Notifications.show({
        intent: Intent.SUCCESS,
        message: `MFA has been reset for ${memberName}`,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset MFA. Try again.');
    } finally {
      setIsPending(false);
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()} isDismissable={!isPending}>
      <Modal variant="drawer" size="small">
        <Dialog aria-label="Reset MFA" className="ResetMfaDrawer-dialog">
          {({ close }) => (
            <>
              <div slot="header">
                <Heading slot="title">Reset MFA</Heading>
                <IconButton
                  aria-label="Close"
                  icon="cancel"
                  size="small"
                  variant="minimal"
                  onPress={close}
                  isDisabled={isPending}
                />
              </div>
              <div slot="body">
                {/* Member info */}
                <div className="ResetMfaDrawer-memberInfo">
                  <Gravatar email={member.email} size={48} />
                  <div>
                    <div className="ResetMfaDrawer-memberName">{memberName}</div>
                    <div className="pt-text-muted">{member.email}</div>
                    <div className="ResetMfaDrawer-memberRole">
                      <TagGroup aria-label="Role">
                        <TagList>
                          <Tag size="small" variant="info">
                            {member.role}
                          </Tag>
                        </TagList>
                      </TagGroup>
                    </div>
                  </div>
                </div>

                {/* Info banner */}
                <div className="ResetMfaDrawer-section">
                  <Alert status="info">
                    {member.mfa === 'disabled' ? (
                      <p>
                        This member does not have MFA enabled. If you reset MFA, {firstName} will be required to enroll
                        in MFA the next time they sign in.
                      </p>
                    ) : (
                      <p>
                        This will invalidate {firstName}&apos;s current MFA configuration. {firstName} will be required
                        to reconfigure MFA the next time they sign in.
                      </p>
                    )}
                  </Alert>
                </div>

                {/* What this does */}
                <div className="ResetMfaDrawer-section">
                  <h4 className="ResetMfaDrawer-sectionTitle">What this does</h4>
                  <div className="ResetMfaDrawer-checklist">
                    {[
                      "Removes the member's current MFA enrollment",
                      'Invalidates existing authenticator app codes',
                      'Generates new recovery codes',
                      'Requires re-enrollment at next sign-in',
                    ].map((item) => (
                      <div key={item} className="ResetMfaDrawer-checklistItem">
                        <Icon name="check" size="small" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Authentication method */}
                <div className="ResetMfaDrawer-section">
                  <h4 className="ResetMfaDrawer-sectionTitle">Authentication method</h4>
                  <AuthMethodTag
                    member={member}
                    samlConfig={account.samlConfig}
                    marketplaceProvider={marketplaceProvider}
                  />
                </div>

                {/* Confirmation warning */}
                <div className="ResetMfaDrawer-section">
                  <Alert status="warning">
                    <p>
                      <strong>Reset MFA for {memberName}?</strong>
                    </p>
                    <p>This will invalidate {firstName}&apos;s current MFA configuration.</p>
                    <p>{firstName} will be required to reconfigure MFA the next time they sign in.</p>
                  </Alert>
                </div>

                {/* Error message */}
                {error && (
                  <div className="ResetMfaDrawer-section">
                    <Alert status="error">
                      <p>{error}</p>
                    </Alert>
                  </div>
                )}
              </div>
              <div slot="footer">
                <div className="ResetMfaDrawer-footer">
                  <Button onPress={close} isDisabled={isPending}>
                    Cancel
                  </Button>
                  <Button variant="destructive" onPress={handleReset} isPending={isPending}>
                    Reset MFA
                  </Button>
                </div>
              </div>
            </>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
